import { smoothScrollIntoView } from '@domgen/base-design-system-2.0/animation/smoothScrollIntoView';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { PosthogFeatureFlags } from '@domgen/shared/enums/featureFlags';
import { gaECommerceEvent, gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { calculateAgeInMonths } from '@domgen/shared/utils/date/calculateAgeInMonths';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { useFeatureFlagEnabled, usePostHog } from '@posthog/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '../../../assets/icons/ArrowLeft';
import { ShieldTickIcon } from '../../../assets/icons/ShieldTick';
import { FormKey } from '../../../enums/formKey';
import { PostHogEvent } from '../../../enums/postHogEvents';
import { StepKey } from '../../../enums/stepKey';
import { StoreEvent } from '../../../enums/storeEvent';
import { QuoteResponse } from '../../../models/services/api/postQuote';
import { controlRoutes } from '../../../routes/controlRoutes';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { useApplianceFormData } from '../../../store/selectors/useApplianceFormData';
import { useAppState } from '../../../store/selectors/useAppState';
import { useGaEventData } from '../../../store/selectors/useGaEventData';
import { useQuote } from '../../../store/selectors/useQuote';
import { useUrlParams } from '../../../store/selectors/useUrlParams';
import { store } from '../../../store/store';
import { getDiscountDisplayData } from '../../../utils/discounts/getDiscountDisplayData';
import { getSystemCode } from '../../../utils/getSystemCode';
import { formApplianceName } from '../../../utils/store/formApplianceName';
import { formCategoryName } from '../../../utils/store/formCategoryName';
import { ApplianceCareTerms } from '../components/ApplianceCareTerms';
import { CallUsModal } from '../components/CallUsModal';
import { OfferBanner } from '../components/OfferBanner';
import { QuoteRadioButton } from '../components/QuoteRadioButton';
import { QuoteUpsellModal } from '../components/QuoteUpsellModal';
import { applianceCareRadioOptionContent } from '../constants/applianceCareRadioOptionContent';
import { chooseApplianceCareArticles } from '../constants/chooseApplianceCareArticles';

export type QuotePageTermTypes = 'appliance-care' | 'manufacturer-warranty';

const styles = {
  container: 'flex-1 flex flex-col items-center desktop:px-30 py-space-500',
  panel:
    'flex flex-col gap-space-500 w-full max-w-300 p-space-500 rounded-radius-100 bg-bg-main-container tablet:gap-space-600',
  header: 'flex flex-col gap-space-500 tablet:gap-space-600',
  heading: 'text-h1 text-text-headline',
  subheading: 'text-body-large text-text-body pb-1',
  options: 'flex flex-col gap-space-200',
  optionsHeading: 'text-body-large-strong text-brand-secondary',
  optionsHeadingError: 'text-body-large-strong text-text-error',
  cards: 'flex flex-col gap-space-500 tablet:gap-space-600',
  card: 'flex flex-col p-space-500 rounded-radius-400 ring-inset cursor-pointer transition-colors',
  manufacturerCard: 'gap-space-100 bg-bg-main-container',
  manufacturerCardBorder: 'ring-1 ring-brand-primary',
  applianceCareCard: 'gap-space-500 bg-primary-50',
  applianceCareCardBorder: 'ring-2 ring-brand-primary',
  cardError: 'ring-2 ring-input-border-error',
  cardHeader:
    'flex flex-col items-start gap-space-400 tablet:flex-row-reverse tablet:items-center tablet:justify-between',
  applianceCareCardHeader: 'pb-space-500 border-b border-primary-200',
  cardTitleRow: 'flex items-center gap-space-300',
  cardTitle: 'text-h3 text-text-headline',
  manufacturerCardSubtitle: 'text-body text-text-body pl-10.75',
  badge: 'inline-flex items-center gap-space-200 h-9 px-space-400 py-space-100 text-h5 shrink-0',
  badgeRegOnly: 'border border-brand-secondary text-brand-secondary',
  badgeProtection: 'bg-success-600 text-neutral-0',
  benefitsList: 'flex flex-col gap-space-400 list-none',
  benefit: 'flex flex-row gap-space-300 items-start',
  benefitIcon: 'text-accent-3-500 shrink-0 w-6 h-6 [&>svg]:w-6 [&>svg]:h-6',
  benefitTitle: 'text-body-large-strong text-text-body',
  benefitDescription: 'text-body-large text-text-body',
  pricing: 'flex flex-col gap-space-200 pt-space-500 border-t border-primary-200',
  discount: 'text-h4 text-text-success',
  price: 'text-h3 text-text-headline',
  priceBeforeDiscount: 'line-through text-text-placeholder mr-space-200',
  planNote: 'text-body-strong text-text-body',
  paymentNote: 'text-body text-text-body',
  footer: 'flex flex-col gap-space-600',
  actionRow:
    'flex flex-col-reverse items-center gap-space-600 tablet:flex-row tablet:justify-between tablet:h-12',
  buttonWrapper: 'w-full tablet:w-fit flex items-center justify-center',
  whyChooseSection:
    'w-full flex flex-col items-center gap-space-600 desktop:px-30 pb-space-700 bg-neutral-0',
  whyChooseHeading: 'text-h3 text-neutral-1000 text-center',
  whyChooseArticles: 'w-full max-w-300 px-space-500 flex flex-col desktop:flex-row gap-space-500',
  whyChooseArticle: 'flex-1 flex flex-col items-center gap-space-400 text-center',
  whyChooseIcon: 'h-11 flex items-center justify-center text-neutral-1000',
};

/**
 * This is  the variant_2_radio for Posthog in quote-page-v2-sep-2026 experiment.
 */

export function QuotePageRadio() {
  const [selectedOption, setSelectedOption] = useState<QuotePageTermTypes | null>(null);
  const [showSelectionError, setShowSelectionError] = useState(false);
  const [displayModal, setDisplayModal] = useState(false);
  const hasSelectionError = showSelectionError && !selectedOption;
  const quote = useQuote();
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const posthog = usePostHog();
  const quoteData = quote?.data as QuoteResponse;
  const gaEventData = useGaEventData() || {};
  const applianceName = formApplianceName.get() ?? '';
  const applianceCategory = formCategoryName.get() ?? '';
  const brand = import.meta.env.VITE_CLIENT_FRIENDLY_NAME;
  const appSessionID = session?.id;
  const { experimentName, experimentVariant, netFirstPaymentAmount: offerPrice } = gaEventData;
  const navigate = useNavigate();

  const purchaseDate = formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE];
  const show2MonthsPromotion =
    useFeatureFlagEnabled(PosthogFeatureFlags.TWO_MONTHS_PROP) &&
    !!purchaseDate &&
    calculateAgeInMonths(purchaseDate) < 3;

  useEffect(() => {
    gaPageView({
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appSessionID: session?.id,
      pagename: 'protection-details',
      offerPrice,
      experimentName,
      experimentVariant,
      quoteId: quoteData.offer.offerReference,
    });
  }, []);

  const discountTerms = getDiscountDisplayData(quoteData);

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const captureQuoteEvent = (eventName: PostHogEvent) =>
    capturePostHogEvent(posthog, eventName, store.get(), {
      quoteId: quoteData.offer.offerReference,
    });

  const selectOption = (option: QuotePageTermTypes) => {
    setSelectedOption(option);

    if (selectedOption !== option) {
      captureQuoteEvent(
        option === 'appliance-care'
          ? 'enhanced-protection-radio-selected'
          : 'warranty-radio-selected',
      );

      gaEvent({
        event: 'genericGAEvent',
        eventCategory: 'AB test Quote ABCD',
        eventAction: 'radio button selected',
        eventLabel:
          option === 'appliance-care' ? 'Whirlpool Appliance Care' : 'Manufacturer warranty only',
        pagename: 'protection-details',
        category: 'registration-pages',
        type: 'STANDARD',
        regSystemCode: systemCode,
        applianceCategory,
        applianceName,
        appSessionID,
        offerPrice,
        experimentName,
        experimentVariant,
        quoteId: quoteData.offer.offerReference,
      });
    }
  };

  const { discountPercentage, discountTerm, periodOfCover, discountPaymentType } = discountTerms;
  const achMarker = discountPaymentType === 'ach' ? '*' : '';
  const discountText =
    discountPercentage && discountTerm && discountTerm < periodOfCover
      ? `Plus, get ${discountPercentage}% off for ${discountTerm} months${achMarker}`
      : `Plus, get ${discountPercentage}% off${achMarker}`;
  const showDiscount = !show2MonthsPromotion && !!discountPercentage;

  const payment = (
    <p className={styles.price}>
      {!!discountTerms.netFirstPaymentAmountAfterPromotionalPeriod &&
        !show2MonthsPromotion &&
        discountTerms.netFirstPaymentAmount <
          discountTerms.netFirstPaymentAmountAfterPromotionalPeriod && (
          <span className={styles.priceBeforeDiscount}>
            {currencyFormatter.format(discountTerms.netFirstPaymentAmountAfterPromotionalPeriod)}
          </span>
        )}
      {currencyFormatter.format(discountTerms.netFirstPaymentAmount)} a month
    </p>
  );

  const offerPanelProps = {
    periodOfCover: discountTerms.periodOfCover,
    discountPercentage: discountTerms.discountPercentage,
    discountTerm: discountTerms.discountTerm,
    netFirstPaymentAmount: discountTerms.netFirstPaymentAmount,
    netFirstPaymentAmountAfterPromotionalPeriod:
      discountTerms.netFirstPaymentAmountAfterPromotionalPeriod,
    discountPaymentType: discountTerms.discountPaymentType,
    show2MonthsPromotion,
    brand,
  };

  const openModal = () => {
    captureQuoteEvent('protection-details-page-register-only-cta-clicked');

    gaPageView({
      event: 'spa-pageview',
      pagename: 'protection-details-popup',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      offerPrice,
      applianceName,
      applianceCategory,
      appSessionID,
      experimentName,
      experimentVariant,
    });

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'protectionDetails_popup_upsell',
      eventLabel: 'popup_presented',
      pagename: 'protection-details-popup',
      category: 'registration-pages',
      type: 'STANDARD',
      offerPrice,
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      quoteId: quoteData.offer.offerReference,
      experimentName,
      experimentVariant,
    });

    setDisplayModal(true);
  };

  const registerOnly = () => {
    store.send({ type: StoreEvent.REG_ONLY });
    captureQuoteEvent('protection-details-modal-register-only-cta-clicked');
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'protection_details_popup_upsell',
      eventLabel: 'No, register with warranty only',
      pagename: 'protection-details-popup',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      quoteId: quoteData.offer.offerReference,
      experimentName,
      experimentVariant,
    });

    setDisplayModal(false);
  };

  const continueToCheckout = () => {
    store.send({
      type: StoreEvent.CONTINUE_TO_CHECKOUT,
      data: { continueToCheckoutFromModal: displayModal },
    });

    captureQuoteEvent(
      displayModal
        ? 'protection-details-modal-monthly-upsell-cta-clicked'
        : 'protection-details-page-monthly-upsell-cta-clicked',
    );

    if (displayModal) {
      gaEvent({
        event: 'genericGAEvent',
        eventCategory: 'appliance details form USA',
        eventAction: 'protectionDetails_popup_upsell',
        eventLabel: 'Yes, I want this protection',
        pagename: 'protection-details-popup',
        category: 'registration-pages',
        type: 'STANDARD',
        regSystemCode: systemCode,
        applianceCategory,
        applianceName,
        appSessionID,
        offerPrice,
        quoteId: quoteData.offer.offerReference,
        experimentName,
        experimentVariant,
      });
    }

    gaECommerceEvent({
      event: 'add_to_cart',
      pagename: displayModal ? 'protection-details-popup' : 'protection-details',
      regSystemCode: systemCode,
      version: 'WiB',
      currency: 'USD',
      appSessionID,
      experimentName,
      experimentVariant,
      quoteId: quoteData.offer.offerReference,
      ecommerce: {
        items: [
          {
            item_brand: brand,
            item_category: applianceCategory,
            item_appliance_type: applianceName,
            item_appliance_purchase_cost: Number(
              formData?.[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
            ),
            item_appliance_purchase_date: formatLocalisedDate(
              parseLocalDateString(formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] || ''),
              'en-US',
            ),
            item_appliance_warranty: true,
            item_name: `${brand} ${applianceName}`,
            price: offerPrice,
            quantity: 1,
            quoteId: quoteData.offer.offerReference,
          },
        ],
      },
    });
  };

  const handleContinue = () => {
    if (!selectedOption) {
      setShowSelectionError(true);
      captureQuoteEvent('protection-details-selection-validation-failed');
      smoothScrollIntoView(0);
      return;
    }

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'protection_details',
      eventLabel: 'Continue with this selection',
      pagename: 'protection-details',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      experimentName,
      experimentVariant,
      selected_flow:
        selectedOption === 'appliance-care'
          ? 'Whirlpool Appliance Care'
          : 'Manufacturer warranty only',
      quoteId: quoteData.offer.offerReference,
    });

    if (selectedOption === 'appliance-care') {
      continueToCheckout();
    } else {
      openModal();
    }
  };

  const handleBack = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      pagename: 'protection-details',
      eventAction: 'GoBack CTA',
      eventLabel: 'success',
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      experimentName,
      experimentVariant,
      quoteId: quoteData.offer.offerReference,
    });
    captureQuoteEvent('price-back-clicked');
    navigate(controlRoutes[controlRoutes.length - 1].path);
  };

  return (
    <>
      <div className={styles.container} data-testid="quote-page-radio">
        <div className={styles.panel}>
          <OfferBanner discountTerms={discountTerms} show2MonthsPromotion={show2MonthsPromotion} />

          <div className={styles.header}>
            <h1 className={styles.heading}>
              Your manufacturer's warranty doesn't cover everything - add extra coverage today
            </h1>
            <p className={styles.subheading}>
              Get extra coverage now and avoid unexpected repair costs.
            </p>
          </div>

          <div className={styles.options}>
            {hasSelectionError ? (
              <h2 className={styles.optionsHeadingError} role="alert">
                Please select your coverage level
              </h2>
            ) : (
              <h2 className={styles.optionsHeading}>Select one to continue</h2>
            )}

            <section className={styles.cards}>
              <article
                className={`${styles.card} ${styles.manufacturerCard} ${hasSelectionError ? styles.cardError : styles.manufacturerCardBorder}`}
                onClick={() => selectOption('manufacturer-warranty')}
              >
                <div className={styles.cardHeader}>
                  <span className={`${styles.badge} ${styles.badgeRegOnly}`}>
                    Registration only
                  </span>
                  <div className={styles.cardTitleRow}>
                    <QuoteRadioButton
                      checked={selectedOption === 'manufacturer-warranty'}
                      onChange={() => selectOption('manufacturer-warranty')}
                      name="cover-option"
                      value="manufacturer-warranty"
                      aria-label="Select Manufacturer warranty"
                    />
                    <h3 className={styles.cardTitle}>Manufacturer warranty</h3>
                  </div>
                </div>
                <p className={styles.manufacturerCardSubtitle}>
                  Your manufacturer warranty covers you for mechanical breakdowns for 12 months
                  only.
                </p>
              </article>

              <article
                className={`${styles.card} ${styles.applianceCareCard} ${hasSelectionError ? styles.cardError : styles.applianceCareCardBorder}`}
                onClick={() => selectOption('appliance-care')}
              >
                <div className={`${styles.cardHeader} ${styles.applianceCareCardHeader}`}>
                  <span className={`${styles.badge} ${styles.badgeProtection}`}>
                    <ShieldTickIcon />
                    Extra coverage
                  </span>
                  <div className={styles.cardTitleRow}>
                    <QuoteRadioButton
                      checked={selectedOption === 'appliance-care'}
                      onChange={() => selectOption('appliance-care')}
                      name="cover-option"
                      value="replacement-care"
                      aria-label="Select Enhanced protection"
                    />
                    <h3 className={styles.cardTitle}>Enhanced protection</h3>
                  </div>
                </div>

                <ul className={styles.benefitsList}>
                  {applianceCareRadioOptionContent.map((benefit, index) => (
                    <li key={index} className={styles.benefit}>
                      <span className={styles.benefitIcon}>
                        <benefit.icon />
                      </span>
                      <p>
                        <span className={styles.benefitTitle}>{benefit.title}</span>
                        <br />
                        <span className={styles.benefitDescription}>{benefit.description}</span>
                      </p>
                    </li>
                  ))}
                </ul>

                <div className={styles.pricing}>
                  {showDiscount && <p className={styles.discount}>{discountText}</p>}
                  {payment}
                  <p className={styles.planNote}>Flexible monthly plan, cancel anytime.</p>
                  <p className={styles.paymentNote}>
                    {discountPaymentType === 'ach' &&
                      showDiscount &&
                      '*Discount applies to payments made via bank account only. '}
                    Applicable taxes calculated during checkout.
                  </p>
                </div>
              </article>
            </section>
          </div>

          <div className={styles.footer}>
            <ApplianceCareTerms
              href={import.meta.env.VITE_APPLIANCE_CARE_TERMS_AND_CONDITIONS_PATH}
              brand={brand}
              variant="plain"
            />

            <section className={styles.actionRow}>
              <TextLink onClick={handleBack} leftIcon={<ArrowLeft />}>
                Go back
              </TextLink>
              <div className={styles.buttonWrapper}>
                <Button onClick={handleContinue}>
                  {selectedOption
                    ? 'Continue with this selection'
                    : 'Select a protection level to continue'}
                </Button>
              </div>
            </section>
          </div>
        </div>
      </div>

      <section className={styles.whyChooseSection}>
        <h2 className={styles.whyChooseHeading}>Why choose {brand} Appliance Care?</h2>
        <div className={styles.whyChooseArticles}>
          {chooseApplianceCareArticles.map((article, index) => (
            <article key={index} className={styles.whyChooseArticle}>
              <figure className={styles.whyChooseIcon}>{article.icon}</figure>
              <p className="text-neutral-1000">{article.description}</p>
            </article>
          ))}
        </div>
      </section>

      <QuoteUpsellModal
        isOpen={displayModal}
        onClose={() => setDisplayModal(false)}
        onRegisterOnly={registerOnly}
        onContinueToCheckout={continueToCheckout}
        offerPanelProps={offerPanelProps}
      />

      <CallUsModal blocked={displayModal} quoteId={quoteData.offer.offerReference} />
    </>
  );
}
